// Justified-rows ("Google Photos" style) layout for the dashboard proof grids.
//
// The problem: proof tiles are camera crops with WILDLY different aspect
// ratios (a shelf crop can be 4:1, a table crop ~1:1, a full frame 16:9). A
// uniform CSS grid forces one cell shape on all of them, and object-contain
// then pads every mismatched image with "curtains" — on a big desktop panel
// most pixels end up background, not photo.
//
// Justified rows fix that by construction: each row gets tiles at their OWN
// natural aspect, scaled to a common row height chosen so the row's total
// width EXACTLY fills the panel. Cell shape == image shape → no curtains.
// Uniform-aspect inputs (full-frame mode) degenerate to a clean uniform grid,
// so one code path serves both display modes.
//
// Two passes:
//  1. Greedy row build at the preferred row height + greedy pagination →
//     decides which items land on which page.
//  2. Per page, probe a ladder of row-height scales (re-flowing the page's
//     items each time), shrink/grow non-justified rows to fit the box, and
//     keep the candidate with the MOST image area — a page with few items
//     zooms up, a dense page packs down to the readability floor.
//
// Pure + deterministic: same items / box / options → same layout, so a
// crop → full → crop round-trip can't shift anything once aspects are cached.

export interface JustifiedItem {
  key: string | number;
  /** Natural width / height of the image (clamped to a sane range). */
  aspect: number;
  /** Problem items — laid out in taller rows (`bigScale`) so they read first. */
  big?: boolean;
}

export interface JustifiedTile {
  key: string | number;
  /** Tile width in px (image box; the caption sits under it full-width). */
  width: number;
}

export interface JustifiedRow {
  tiles: JustifiedTile[];
  /** Image height in px (caption excluded). */
  imgH: number;
  /**
   * true → the row exactly fills the panel width (render edge-to-edge);
   * false → an underfull trailing row (tiles keep natural width, centered).
   */
  fill: boolean;
}

export interface JustifiedPage {
  rows: JustifiedRow[];
  /** Total content height incl. captions + inter-row gaps (≤ box height). */
  usedH: number;
}

export interface JustifyOpts {
  /** Content box, px. */
  width: number;
  height: number;
  /** Gap between tiles and between rows, px. */
  gap: number;
  /** Caption strip under each image (name + status dot), px. */
  captionH: number;
  /** Readability floor for the image height — denser than this paginates. */
  minImgH: number;
  /** Zoom ceiling (a near-empty page won't blow a tile past this). */
  maxImgH: number;
  /** Preferred image height — sets the page granularity before pass 2. */
  targetImgH: number;
  /** Row-height multiplier for rows led by `big` (problem) items. */
  bigScale?: number;
}

interface NormItem extends JustifiedItem {
  aspect: number; // clamped
}

const ASPECT_MIN = 0.3;
const ASPECT_MAX = 5;

function clampAspect(a: number): number {
  if (!Number.isFinite(a) || a <= 0) return 4 / 3;
  return Math.min(ASPECT_MAX, Math.max(ASPECT_MIN, a));
}

interface BuiltRow extends JustifiedRow {
  items: NormItem[];
}

// Greedy row builder at row-image-height H: take tiles until the row (at H)
// reaches the panel width, closing on whichever side of the boundary item is
// the closer fit. A row that fills gets the exact justified height
// (usable / Σaspect); a trailing underfull row keeps H (never stretched).
function buildRows(itemsIn: NormItem[], H: number, o: JustifyOpts): BuiltRow[] {
  // Local copy: the underfull-row rescue below reorders the tail, and the
  // caller re-invokes buildRows with the same array across the pass-2 ladder.
  const items = [...itemsIn];
  const bigScale = o.bigScale ?? 1.35;
  const rows: BuiltRow[] = [];
  let i = 0;
  while (i < items.length) {
    const target = Math.min(items[i].big ? H * bigScale : H, o.maxImgH);
    let sumA = 0;
    let j = i;
    while (j < items.length) {
      const a = items[j].aspect;
      const mWith = j - i + 1;
      const usableWith = o.width - o.gap * (mWith - 1);
      if (sumA > 0 && (sumA + a) * target > usableWith) {
        // Boundary item: keep it only if overshooting is closer than undershooting.
        const overWith = (sumA + a) * target - usableWith;
        const usableWithout = o.width - o.gap * (mWith - 2);
        const underWithout = usableWithout - sumA * target;
        if (overWith < underWithout) {
          sumA += a;
          j++;
        }
        break;
      }
      sumA += a;
      j++;
      if (sumA * target >= usableWith) break;
    }
    // Underfull-row rescue: the NEXT item was too wide, but a LATER, narrower
    // item may still fit — pull the widest such items forward (greedy). The
    // aspect-sorted input strands rows otherwise: a ~1:1 problem tile followed
    // by a 3:1 shelf strip leaves the problem row ~half blank (zone 21), while
    // a later ~1:1 tile would have filled it edge-to-edge. OK-tile order is
    // explicitly free to optimise (see packOrder).
    if (j < items.length && sumA * target < o.width - o.gap * (j - i - 1) - 0.5) {
      for (;;) {
        const usableWith = o.width - o.gap * (j - i);
        let bestK = -1;
        for (let k = j; k < items.length; k++) {
          const a = items[k].aspect;
          if ((sumA + a) * target <= usableWith + 0.5) {
            if (bestK < 0 || a > items[bestK].aspect) bestK = k;
          }
        }
        if (bestK < 0) break;
        const [pulled] = items.splice(bestK, 1);
        items.splice(j, 0, pulled);
        sumA += pulled.aspect;
        j++;
      }
    }
    const rowItems = items.slice(i, j);
    const m = rowItems.length;
    const usable = o.width - o.gap * (m - 1);
    const fill = sumA * target >= usable - 0.5;
    let imgH = fill ? usable / sumA : target;
    // Floors/ceilings: never paint an unreadably short or absurdly tall row.
    imgH = Math.min(Math.max(imgH, o.minImgH * 0.75), o.maxImgH);
    // A filled row's widths must still sum to `usable` even after clamping —
    // distribute proportionally to aspects rather than a*imgH.
    const tiles: JustifiedTile[] = fill
      ? rowItems.map(it => ({ key: it.key, width: (it.aspect / sumA) * usable }))
      : rowItems.map(it => ({ key: it.key, width: Math.min(it.aspect * imgH, usable) }));
    rows.push({ tiles, imgH, fill, items: rowItems });
    i = j;
  }
  return rows;
}

function totalHeight(rows: BuiltRow[], o: JustifyOpts): number {
  if (rows.length === 0) return 0;
  return (
    rows.reduce((s, r) => s + r.imgH + o.captionH, 0) + o.gap * (rows.length - 1)
  );
}

// Objective for pass 2: photo surface, with big (problem) tiles counted
// double — and a hard haircut when any big-carrying row ends up SHORTER than
// a plain row, so "problems read bigger" survives the area maximisation.
// Each row's contribution is scaled by its span² (row width / panel width):
// raw area let a tall-but-centered solo tile out-score a pairing that spans
// the panel — bringing back exactly the "large side margins" the layout is
// supposed to eliminate. Span² keeps edge-to-edge groupings winning while
// still letting a genuinely unpairable trailing row live.
function scoreRows(rows: BuiltRow[], o: JustifyOpts): number {
  let score = 0;
  let minBigH = Infinity;
  let maxPlainH = 0;
  let midUnderfull = false;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const hasBig = r.items.some(it => it.big);
    if (hasBig) minBigH = Math.min(minBigH, r.imgH);
    else maxPlainH = Math.max(maxPlainH, r.imgH);
    const visualW =
      r.tiles.reduce((s, t) => s + t.width, 0) + o.gap * (r.tiles.length - 1);
    const span = Math.min(1, visualW / o.width);
    // A centered underfull row in the MIDDLE of a page reads as broken
    // ("floating tile with curtains" — user-reported): only the trailing row
    // may legitimately stay underfull. Near-full (justified rounding) passes.
    if (i < rows.length - 1 && span < 0.95) midUnderfull = true;
    for (let k = 0; k < r.tiles.length; k++) {
      score += r.imgH * r.tiles[k].width * (r.items[k].big ? 2 : 1) * span * span;
    }
  }
  if (minBigH < maxPlainH - 0.5) score *= 0.4;
  if (midUnderfull) score *= 0.15;
  // Ряд-«сирота»: одно-две крошечные фото на целый ряд под крупными (Алексей 20.10 #1631).
  // Ряд, который ниже половины самого высокого обычного ряда, — сильный штраф.
  if (rows.length > 1 && maxPlainH > 0 && rows.some(r => !r.items.some(it => it.big) && r.imgH < maxPlainH * 0.5)) {
    score *= 0.2;
  }
  // Фото-«марка»: плитка, у которой короткая сторона меньше ~12 % бокса, нечитаема — время,
  // галка и подпись слипаются в кашу (Астра 24.10, Алексей #2281: крупный стеллаж на весь
  // ряд, второй — миниатюрой 70 px под ним). Штраф сильнее любого выигрыша по площади.
  const minSide = Math.max(72, 0.12 * Math.min(o.width, o.height));
  if (rows.some(r => r.tiles.some(t => Math.min(t.width, r.imgH) < minSide))) score *= 0.05;
  return score;
}

// Re-height an UNDERFULL row (tile widths follow the aspect; flips to `fill`
// when the new height makes the row span the panel width).
function setRowHeight(row: BuiltRow, imgH: number, o: JustifyOpts): void {
  const sumA = row.items.reduce((s, it) => s + it.aspect, 0);
  const usable = o.width - o.gap * (row.items.length - 1);
  row.imgH = Math.min(imgH, usable / sumA, o.maxImgH);
  row.fill = row.imgH >= usable / sumA - 0.5;
  row.tiles = row.items.map(it => ({ key: it.key, width: it.aspect * row.imgH }));
}

// Make a candidate page fit its box: shrink trailing non-justified rows when
// over, then grow non-justified rows into the leftover when under (justified
// rows are width-locked — their height can't change without curtains).
// Exception: the LAST row may be shrunk even when justified — it degrades to
// a centered underfull trailing row, which is legitimate (and unlocks the
// "full-width rows + smaller trailing row" layouts that raw building can't
// produce: at large scales every row closes as width-fill and the page would
// otherwise be discarded as unfittable).
// Returns null when the page can't fit even after shrinking.
function adjustToFit(rows: BuiltRow[], o: JustifyOpts): BuiltRow[] | null {
  let total = totalHeight(rows, o);
  for (let k = rows.length - 1; k >= 0 && total > o.height + 0.5; k--) {
    const r = rows[k];
    if (r.fill && k !== rows.length - 1) continue;
    const excess = total - o.height;
    const newH = Math.max(o.minImgH * 0.75, r.imgH - excess);
    if (newH >= r.imgH) continue;
    total -= r.imgH - newH;
    setRowHeight(r, newH, o);
  }
  if (total > o.height + 0.5) return null;
  for (const r of rows) {
    if (total >= o.height - 0.5) break;
    if (r.fill) continue;
    const newH = r.imgH + (o.height - total);
    const before = r.imgH;
    setRowHeight(r, newH, o); // caps at fill-width / maxImgH internally
    total += r.imgH - before;
  }
  return rows;
}

// Greedy pagination: rows flow into a page while they fit its height.
function paginate(rows: BuiltRow[], o: JustifyOpts): BuiltRow[][] {
  const pages: BuiltRow[][] = [];
  let cur: BuiltRow[] = [];
  let used = 0;
  for (const row of rows) {
    const outer = row.imgH + o.captionH;
    const add = (cur.length ? o.gap : 0) + outer;
    if (cur.length && used + add > o.height + 0.5) {
      pages.push(cur);
      cur = [];
      used = 0;
    }
    cur.push(row);
    used += (cur.length > 1 ? o.gap : 0) + outer;
  }
  if (cur.length) pages.push(cur);
  return pages;
}

/**
 * Lay `items` (in the given order — sort upstream) out into justified pages
 * for a `width`×`height` box. Returns [] for empty/unmeasured input.
 */
export function justifiedPages(itemsIn: JustifiedItem[], optsIn: JustifyOpts): JustifiedPage[] {
  if (optsIn.width <= 0 || optsIn.height <= 0 || itemsIn.length === 0) return [];
  const o: JustifyOpts = {
    ...optsIn,
    // A row can never be taller than the page allows for one row.
    maxImgH: Math.max(
      optsIn.minImgH,
      Math.min(optsIn.maxImgH, optsIn.height - optsIn.captionH),
    ),
  };
  o.targetImgH = Math.min(Math.max(o.targetImgH, o.minImgH), o.maxImgH);
  const items: NormItem[] = itemsIn.map(it => ({ ...it, aspect: clampAspect(it.aspect) }));

  // Pass 1 — page membership at the preferred height.
  const membership = paginate(buildRows(items, o.targetImgH, o), o);

  // Pass 2 — per page, probe row-height scales (the fill function is jagged:
  // a tiny scale bump can re-flow a row and jump the height, so a ladder beats
  // a binary search), fit each candidate to the box, keep the max image area.
  //
  // The bigScale sub-ladder exists because a FILL row's height is fixed by its
  // grouping (usable/Σaspect) — the scale only changes which tiles land in
  // which row, and one uniform multiplier can't reach groupings like "problem
  // tile solo, the rest in a tight trio" (that needs the big target ≈2.6× the
  // plain target). Without it a 4-tile page settled for two fill rows using
  // ~57% of the panel height (zone 47 Выкладка).
  const baseBig = o.bigScale ?? 1.35;
  return membership.map(pageRows => {
    const pageItems = pageRows.flatMap(r => r.items);
    const bigScales = pageItems.some(it => it.big) ? [baseBig, 2.0, 2.6] : [baseBig];
    let best: { rows: BuiltRow[]; area: number } | null = null;
    for (const bigScale of bigScales) {
      const po = { ...o, bigScale };
      for (let scale = 0.5; scale <= 3.01; scale += 0.125) {
        const rows = adjustToFit(buildRows(pageItems, o.targetImgH * scale, po), po);
        if (!rows) continue;
        const area = scoreRows(rows, po);
        if (!best || area > best.area + 0.5) best = { rows, area };
      }
    }
    if (!best) {
      // Even the smallest scale overflowed (pathological box) — take the
      // smallest-scale rows and let the page clip the bottom.
      best = { rows: buildRows(pageItems, o.targetImgH * 0.5, o), area: 0 };
    }
    return {
      rows: best.rows.map(({ tiles, imgH, fill }) => ({ tiles, imgH, fill })),
      usedH: totalHeight(best.rows, o),
    };
  });
}

/**
 * Всё на одну страницу, без листания (не-мобильная раскладка, Алексей 20.10 #1631):
 * перебираем высоту ряда от мелкой до крупной, каждую подгоняем под бокс и берём
 * вариант с наибольшей площадью фото. Если всё влезает крупно — фото крупные; если нет —
 * мельчают, но всё равно все на экране. Пол — `floorImgH` (по умолчанию 28 px).
 */
export function justifiedFitAll(itemsIn: JustifiedItem[], optsIn: JustifyOpts, floorImgH = 28): JustifiedPage[] {
  if (optsIn.width <= 0 || optsIn.height <= 0 || itemsIn.length === 0) return [];
  const o: JustifyOpts = {
    ...optsIn,
    minImgH: Math.min(optsIn.minImgH, floorImgH * 1.34),
    maxImgH: Math.max(floorImgH, Math.min(optsIn.maxImgH, optsIn.height - optsIn.captionH)),
  };
  const items: NormItem[] = itemsIn.map(it => ({ ...it, aspect: clampAspect(it.aspect) }));
  const baseBig = o.bigScale ?? 1.35;
  const bigScales = items.some(it => it.big) ? [baseBig, 2.0, 2.6] : [baseBig];
  let best: { rows: BuiltRow[]; area: number } | null = null;
  for (const bigScale of bigScales) {
    const po = { ...o, bigScale };
    for (let h = floorImgH; h <= o.maxImgH + 0.5; h *= 1.08) {
      const rows = adjustToFit(buildRows(items, h, po), po);
      if (!rows) continue;
      const area = scoreRows(rows, po);
      if (!best || area > best.area + 0.5) best = { rows, area };
    }
  }
  if (!best) best = { rows: buildRows(items, floorImgH, o), area: 0 };
  return [{
    rows: best.rows.map(({ tiles, imgH, fill }) => ({ tiles, imgH, fill })),
    usedH: totalHeight(best.rows, o),
  }];
}

/**
 * Order units for packing: problems first (by severity rank), then OK items
 * grouped by similar aspect (descending) so justified rows pack tightly —
 * the user-visible order of the green "all fine" tiles is free to optimise.
 */
export function packOrder<T extends { hasProblem: boolean; rank: number }>(
  units: T[],
  aspectOf: (u: T) => number,
): T[] {
  return [...units].sort((a, b) => {
    if (a.hasProblem !== b.hasProblem) return a.hasProblem ? -1 : 1;
    if (a.hasProblem) return a.rank - b.rank;
    const d = aspectOf(b) - aspectOf(a);
    if (Math.abs(d) > 0.05) return d;
    return a.rank - b.rank;
  });
}
